import { useId, type FormEvent, type ReactNode } from "react";
import { buttonClass } from "@/components/common/controls";
import { ArrowRightIcon } from "@/components/icons";
import { actionProps } from "@/lib/controlAnalytics";
import { jobAddress, type PastedJob } from "@/lib/tools/pastedJob";
import SavedResume from "./SavedResume";
import type { TailorAnswers, TailorValues } from "./useTailorAnswers";

/** The longest title and company envoy keeps: `MAX_TITLE_CHARS` and `MAX_COMPANY_CHARS`. */
const MAX_TITLE = 300;
const MAX_COMPANY = 200;
const INPUT =
    "box-border h-11 w-full rounded-control border-[1.5px] border-line-strong bg-card-raised px-3.5 text-[14px] text-fg outline-none placeholder:text-fg-faint focus:border-green focus:shadow-[0_0_0_3px_var(--color-tint-green)] aria-[invalid=true]:border-negative";

function Field({
    id,
    label,
    error,
    hint,
    children,
}: {
    id: string;
    label: string;
    error: string | null;
    hint?: string;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={id} className="text-[12.5px] font-semibold text-fg">
                {label}
            </label>
            {children}
            {error ? (
                <span
                    id={`${id}-note`}
                    role="alert"
                    className="text-[12.5px] font-medium text-negative"
                >
                    {error}
                </span>
            ) : (
                hint && (
                    <span id={`${id}-note`} className="text-[12.5px] text-fg-faint">
                        {hint}
                    </span>
                )
            )}
        </div>
    );
}

/** The three questions and Continue. Nothing is sent until each is answered. */
export default function TailorFields({
    answers,
    description,
    resume,
    member,
    onSubmit,
}: {
    answers: TailorAnswers;
    description: string;
    resume: string;
    member: boolean;
    onSubmit: (job: PastedJob) => void;
}) {
    const ids = useId();
    const { values, problems, set, setProblems } = answers;
    const context = { source: "TailorForm" };

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const found = jobAddress(values.link);
        setProblems({
            ...(values.title.trim() === "" ? { title: "Name the job's title." } : {}),
            ...(values.company.trim() === "" ? { company: "Name the company." } : {}),
            ...(found.problem !== null ? { link: found.problem } : {}),
        });
        if (found.address === null || values.title.trim() === "" || values.company.trim() === "") {
            return;
        }
        onSubmit({
            title: values.title.trim(),
            company: values.company.trim(),
            address: found.address,
            description,
            resume,
        });
    };
    const input = (name: keyof TailorValues, max?: number) => (
        <input
            {...actionProps("tool_tailor_job_edited", { ...context, field: name })}
            id={`${ids}-${name}`}
            value={values[name]}
            maxLength={max}
            type={name === "link" ? "url" : "text"}
            autoComplete="off"
            aria-invalid={problems[name] ? true : undefined}
            aria-describedby={`${ids}-${name}-note`}
            onChange={(event) => set(name, event.target.value)}
            className={INPUT}
        />
    );

    return (
        <form
            {...actionProps("tool_tailor_submitted", context)}
            onSubmit={submit}
            noValidate
            className="flex flex-col gap-3.5"
        >
            <Field id={`${ids}-title`} label="Job title" error={problems.title ?? null}>
                {input("title", MAX_TITLE)}
            </Field>
            <Field id={`${ids}-company`} label="Company" error={problems.company ?? null}>
                {input("company", MAX_COMPANY)}
            </Field>
            <Field
                id={`${ids}-link`}
                label="Job link"
                error={problems.link ?? null}
                hint="The address of the posting. We add https:// if it is missing."
            >
                {input("link")}
            </Field>
            {member && <SavedResume />}
            <button
                {...actionProps("tool_tailor_continue_clicked", context)}
                type="submit"
                className={buttonClass("primary", "tall", "w-full gap-2")}
            >
                Continue
                <ArrowRightIcon className="size-3.5" />
            </button>
        </form>
    );
}
